import { MapProvider } from './map-provider-interface.js';

/**
 * OSM/Leaflet-Implementierung des MapProvider-Vertrags (Stufe 1 des Konzepts).
 * Benoetigt die selbst gehostete Leaflet-Bibliothek unter Resources/Public/Contrib/Leaflet/
 * (siehe README dort - noch manuell zu ergaenzen, kein Internetzugriff beim Erstellen dieses Grundgeruests).
 */
export class LeafletOsmProvider extends MapProvider {
    #map = null;
    // Bleibt null ohne options.cluster (z. B. Detailkarte mit nur einem Marker) - addMarker()/
    // removeMarker()/showMarker() fallen dann auf #map zurueck, siehe #markerLayer().
    #clusterLayer = null;

    init(container, options) {
        if (typeof window.L === 'undefined') {
            console.warn('Chip Mäp: Leaflet (window.L) ist nicht geladen - siehe Resources/Public/Contrib/Leaflet/README.md');
            return;
        }

        this.#map = window.L.map(container, { zoomControl: false }).setView(
            [options.centerLat, options.centerLng],
            options.zoom
        );

        window.L.control.zoom({
            zoomInTitle: container.dataset.labelZoomIn || 'Zoom in',
            zoomOutTitle: container.dataset.labelZoomOut || 'Zoom out',
        }).addTo(this.#map);

        if (/^de(?:-|$)/i.test(document.documentElement.lang)) {
            this.#map.on('popupopen', ({ popup }) => {
                popup.getElement()?.querySelector('.leaflet-popup-close-button')
                    ?.setAttribute('aria-label', 'Popup schließen');
            });
        }

        window.L.tileLayer(options.tileUrl, {
            attribution: options.attribution,
            maxZoom: 19,
        }).addTo(this.#map);

        if (options.cluster) {
            if (typeof window.L.markerClusterGroup !== 'function') {
                console.warn('Chip Mäp: Leaflet.markercluster ist nicht geladen - siehe Resources/Public/Contrib/MarkerCluster/README.md');
            } else {
                this.#clusterLayer = window.L.markerClusterGroup({
                    maxClusterRadius: 50,
                    spiderfyOnMaxZoom: true,
                    showCoverageOnHover: false,
                    iconCreateFunction: (cluster) => window.L.divIcon({
                        className: 'cc-chipmap__marker-cluster',
                        html: `<span>${cluster.getChildCount()}</span>`,
                        iconSize: [36, 36],
                        iconAnchor: [18, 18],
                    }),
                });
                this.#map.addLayer(this.#clusterLayer);
            }
        }
    }

    // Ziel-Layer fuer addMarker()/removeMarker()/showMarker(): bei aktiviertem Clustering die
    // Cluster-Gruppe (damit Leaflet.markercluster die Zugehoerigkeit fuer die Zahlen-Bubbles kennt),
    // sonst direkt die Karte - marker.addTo(...) funktioniert fuer beide identisch (beide sind
    // Leaflet-Layer mit addLayer()).
    #markerLayer() {
        return this.#clusterLayer || this.#map;
    }

    addMarker(location) {
        if (!this.#map) {
            return null;
        }

        const icon = location.iconUrl
            ? window.L.icon({
                  iconUrl: location.iconUrl,
                  iconSize: [32, 32],
                  iconAnchor: [16, 32],
                  popupAnchor: [0, -32],
              })
            : window.L.divIcon({
                  className: 'cc-chipmap__marker-dot',
                  html: `<span style="background:${location.color || '#2A2623'}"></span>`,
                  iconSize: [24, 24],
                  iconAnchor: [12, 12],
              });

        const marker = window.L.marker([location.lat, location.lng], { icon });
        const isGerman = /^de(?:-|$)/i.test(document.documentElement.lang);
        const labelPrefix = isGerman ? 'Standort' : 'Location';
        const actionLabel = isGerman ? 'Details anzeigen' : 'Show details';
        const openWithKeyboard = (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                marker.openPopup();
            }
        };
        const closeWithKeyboard = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                marker.closePopup();
            }
        };
        marker.on('add', () => {
            const element = marker.getElement();
            element?.setAttribute('aria-label', `${labelPrefix}: ${location.name}. ${actionLabel}`);
            if (location.popupHtml) {
                element?.setAttribute('aria-haspopup', 'dialog');
                element?.addEventListener('keydown', openWithKeyboard);
            }
        });
        if (location.popupHtml) {
            marker.bindPopup(location.popupHtml);
            marker.on('popupopen', ({ popup }) => {
                const element = popup.getElement();
                element.setAttribute('role', 'dialog');
                element.setAttribute('aria-label', `${labelPrefix}: ${location.name}`);
                element.setAttribute('tabindex', '-1');
                element.addEventListener('keydown', closeWithKeyboard);
                element.focus({ preventScroll: true });
            });
            marker.on('popupclose', () => {
                marker.getElement()?.focus({ preventScroll: true });
            });
        }
        marker.addTo(this.#markerLayer());
        return marker;
    }

    removeMarker(marker) {
        if (marker && this.#map) {
            this.#markerLayer().removeLayer(marker);
        }
    }

    showMarker(marker) {
        if (marker && this.#map) {
            marker.addTo(this.#markerLayer());
        }
    }

    fitToMarkers(markers) {
        if (!this.#map || !markers || markers.length === 0) {
            return;
        }
        const group = window.L.featureGroup(markers);
        this.#map.fitBounds(group.getBounds().pad(0.15));
    }

    openPopup(marker) {
        if (marker) {
            marker.openPopup();
            this.#map.panTo(marker.getLatLng());
        }
    }

    async addGeoJsonOverlay(url, style = {}) {
        if (!this.#map) {
            return null;
        }
        try {
            const response = await fetch(url);
            const data = await response.json();
            const layer = window.L.geoJSON(data, {
                style: () => ({
                    color: style.color || '#2A2623',
                    weight: style.weight ?? 3,
                    fill: false,
                }),
            }).addTo(this.#map);
            layer.bringToBack();
            return layer;
        } catch (error) {
            console.warn('Chip Mäp: Regions-Umriss konnte nicht geladen werden.', error);
            return null;
        }
    }
}
